// this the form to be used when creating a NEW incoming delivery
import React, {useState} from 'react'
import {makeStyles} from '@mui/styles'
import Grid from '@mui/material/Grid'
import {Colors, Typography as TypographyStyle} from '../../assets/styles'
import SearchResident from '../SearchResident'
import SelectInput from '../SelectInput'
import Constants from './DeliveryConstants'
import TextInput from '../TextInput'
import MultilineTextInput from '../MultilineTextInput'
import CustomCheckbox from '../CustomCheckbox'
import PropTypes from 'prop-types'
import Constant from './DeliveryConstants'
import {CreateNewDelivery} from '../../lib/queries'
import Routes from '../../constants/RouteConstants'
import RXRButton from '../RXRButton'
import {useHistory} from 'react-router-dom'
import {useSelector} from 'react-redux'
import useDoOnceTimer from '../hooks/useDoOnceTimer'
import GenericManyInput from '../GenericManyInput'
import RXRIcon from '../RXRIcon'
import SimpleSpinner from '../SimpleSpinner'
import {selectAuthedUserId} from '../../reducers/selectors'
import {pluralizeWord} from '../../Utils/StringFormatter'

const NOTES_CHARACTER_LIMIT = 200

const STATUS_SAVING = 'SAVING'
const STATUS_SUCCESS = 'SUCCESS'
const STATUS_ERROR = 'ERROR'

/**
 * @typedef {Object} ResidentEntryDetails
 * @property {string} residentId
 * @property {string} unitId
 * @property {string} trackingNumber
 */

const useStyles = makeStyles(theme => ({
  heading: {
    ...TypographyStyle.H3Header,
    lineHeight: '22px',
    marginBottom: '18px',
    color: '#431A1A',
  },
  priorityGrid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    textAlign: 'center',
  },
  priorityHeading: {
    ...TypographyStyle.BodyHeader,
    textAlign: 'left',
  },
  priorityHeadingDisabled: {
    ...TypographyStyle.BodyHeader,
    color: Colors.darkGrey,
    textAlign: 'left',
  },
  loggedBy: {
    ...TypographyStyle.BodyHeader,
    color: Colors.rxrBlackColor,
    textAlign: 'left',
  },

  manyInputContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}))

// ------------------------------------------------------------------------------------

function TenantAndTrackingNumberInput({value, onChange, index, error, isDisabled}) {
  return (
    <React.Fragment>
      <div style={{width: '100%'}}>
        <TextInput
          label="Tracking number"
          placeholder=""
          maxLength={120}
          onChange={val => onChange({...value, trackingNumber: val})}
          value={value.trackingNumber}
          isDisabled={isDisabled}
          helperText={error || index > 0 ? ' ' : undefined}
          error={!!error}
        />
      </div>
      <div style={{width: '100%'}}>
        <SearchResident
          error={!!error}
          disabled={isDisabled}
          value={value.residentId}
          isRequired={true}
          onSelectResident={r =>
            r ? onChange({...value, residentId: r.id, unitId: r.occupancy.unit.id}) : onChange({...value, residentId: null, unitId: null})
          }
        />
      </div>
    </React.Fragment>
  )
}

TenantAndTrackingNumberInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  index: PropTypes.number,
  error: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

// ------------------------------------------------------------------------------------

const entryLevelProps = ['residentId', 'unitId', 'trackingNumber']
const topLevelProps = ['carrier', 'packageType', 'notes', 'priority']

export default function NewIncomingDeliveriesForm(props) {
  const classes = useStyles()
  /** @type {Array<ResidentEntryDetails>} */
  const [residentAndTrackingNumbers, setResidentAndTrackingNumbers] = useState([])
  const [invalidItems, setInvalidItems] = useState({})
  const [savingItems, setSavingItems] = useState({})

  const authedUserId = useSelector(selectAuthedUserId)
  const activeBuildingId = useSelector(state => state.Buildings.activeBuildingId)

  const createNewPackages = () => {
    if (residentAndTrackingNumbers.length === 0) {
      // this is how we indicate the input box should show an error
      setInvalidItems({residentId: true})
      return
    }

    const packageInputs = residentAndTrackingNumbers.map(details => ({
      buildingId: activeBuildingId,
      carrier: props.carrier,
      notes: props.notes,
      packageType: props.packageType,
      priority: props.priority,
      trackingNumber: details.trackingNumber,
      residentId: details.residentId,
      unitId: details.unitId,
      staffId: authedUserId,

      // this is set by the GenericManyInput and is used to identify the entry across different lists
      rowId: details.rowId,
    }))

    let newInvalidItems = null
    packageInputs.forEach((input, i) => {
      const validationResults = props.validationFunction(input)
      if (validationResults.length > 0) {
        newInvalidItems = newInvalidItems || {invalidResidentDetails: new Array(residentAndTrackingNumbers.length).fill(false)}

        validationResults.forEach(propName => {
          if (topLevelProps.includes(propName)) {
            newInvalidItems[propName] = true
          } else if (entryLevelProps.includes(propName)) {
            newInvalidItems.invalidResidentDetails[i] = true
          }
        })
      }
    })

    if (newInvalidItems) {
      setInvalidItems(newInvalidItems)
      return
    }

    // at this point, all our details are ready to rock

    // determine which items are ready to save and not already saved
    const itemsToSave = packageInputs.filter(details => !savingItems[details.rowId] || savingItems[details.rowId] === STATUS_ERROR)

    // update our saving items state so we can show the correct icons
    setSavingItems(prev =>
      Object.values(itemsToSave).reduce(
        (agr, input) => {
          agr[input.rowId] = STATUS_SAVING
          return agr
        },
        {...prev},
      ),
    )

    // now we attempt to save all our outstanding items
    Promise.allSettled(
      itemsToSave.map(input => {
        // we save everything but the rowId
        const {rowId, ...finalInput} = input
        return CreateNewDelivery(finalInput)
          .then(d => {
            // if this item saved, update its status to success
            setSavingItems(p => ({...p, [input.rowId]: STATUS_SUCCESS}))
            return d
          })
          .catch(err => {
            // if it failed, indicate it has an error
            setSavingItems(p => ({...p, [input.rowId]: STATUS_ERROR}))
            throw err
          })
      }),
    ).then(r => {
      const succeeded = r.filter(p => p.status === 'fulfilled').map(p => p.value)
      // if any of them saved, we reload deliveries
      if (succeeded.length > 0) {
        const areAllSaved = succeeded.length === r.length
        props.onSaveComplete(succeeded, areAllSaved)
      }
    })
  }

  // --------------------------------------------------------------------------------------------

  const isSaving = Object.values(savingItems).some(s => s === STATUS_SAVING)
  const countSaved = Object.values(savingItems).filter(s => s === STATUS_SUCCESS).length
  const countToSave = residentAndTrackingNumbers.length - countSaved

  return (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item md={6} xs={12}>
          <SelectInput
            onChange={val =>
              props.updateForm({
                carrier: val,
              })
            }
            disabled={isSaving}
            label={'Carrier'}
            isRequired={true}
            options={Constants.CARRIER_OPTIONS}
            value={props.carrier}
            error={Object.prototype.hasOwnProperty.call(invalidItems, 'carrier')}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <SelectInput
            onChange={val =>
              props.updateForm({
                packageType: val,
              })
            }
            disabled={isSaving}
            label={'Package type'}
            isRequired={true}
            options={Constants.PACKAGE_OPTIONS}
            value={props.packageType}
            error={Object.prototype.hasOwnProperty.call(invalidItems, 'packageType')}
          />
        </Grid>

        <Grid item xs={12}>
          <MultilineTextInput
            label="Additional details"
            placeholder=""
            helperText={`The resident will see these notes. ${NOTES_CHARACTER_LIMIT} character limit.`}
            rows={6}
            rowsMax={6}
            isDisabled={isSaving}
            maxLength={NOTES_CHARACTER_LIMIT}
            onChange={val => props.updateForm({notes: val})}
            value={props.notes}
          />
        </Grid>
        <Grid item xs={12} className={classes.priorityGrid}>
          <CustomCheckbox
            isDisabled={isSaving}
            isChecked={props.priority === 'HIGH'}
            onChange={checked =>
              props.updateForm({
                priority: checked ? 'HIGH' : 'LOW',
              })
            }
            label={'High priority?'}
          />
        </Grid>
      </Grid>

      <div className={classes.manyInputContainer}>
        <GenericManyInput
          hideNewInput={isSaving}
          validator={o => {
            // only resident and unit required
            return o.residentId && o.unitId
          }}
          onChange={setResidentAndTrackingNumbers}
          value={residentAndTrackingNumbers}
          emptyModel={{residentId: null, unitId: null, trackingNumber: ''}}
          showPlusIcon={true}
          erroredEntries={invalidItems.invalidResidentDetails}
          error={!!invalidItems.residentId}
          disabledEntries={residentAndTrackingNumbers.map(e => [STATUS_SAVING, STATUS_SUCCESS].includes(savingItems[e.rowId]))}
          singleInputComponent={TenantAndTrackingNumberInput}
          iconOverride={entry => {
            if (savingItems[entry.rowId]) {
              switch (savingItems[entry.rowId]) {
                case STATUS_SAVING:
                  return <SimpleSpinner />

                case STATUS_SUCCESS:
                  return <RXRIcon icon={RXRIcon.CHECK} color={Colors.rxrSuccessColor} size={RXRIcon.SIZE_LARGE} />

                case STATUS_ERROR:
                  return <RXRIcon icon={RXRIcon.CLOSE} color={Colors.rxrErrorColor} size={RXRIcon.SIZE_LARGE} />
              }
            }
            return null
          }}
        />
      </div>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <RXRButton onClick={createNewPackages} disabled={isSaving || countToSave === 0}>
            {Constant.BTN_SAVE} {residentAndTrackingNumbers.length > 0 ? pluralizeWord('package', countToSave, true) : ''}
          </RXRButton>
        </Grid>
        <Grid item xs={6}>
          <RXRButton type={RXRButton.TYPE_TEXT} onClick={props.onCancel} disabled={isSaving}>
            {countSaved > 0 ? 'Close' : Constants.BTN_CANCEL}
          </RXRButton>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

NewIncomingDeliveriesForm.propTypes = {
  // these are form fields
  priority: PropTypes.string,
  carrier: PropTypes.string,
  packageType: PropTypes.string,
  notes: PropTypes.string,

  updateForm: PropTypes.func.isRequired,
  validationFunction: PropTypes.func.isRequired,
  onSaveComplete: PropTypes.func.isRequired, // this will be called after at least one delivery saves and will pas 2 args: the array if newly saved packages, abd True IFF all packages saved successfully
  onCancel: PropTypes.func.isRequired, // this will be called when the user clicks cancel or closes the panel
}
